import React, { useState, useEffect } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import IdiomsGridEditor from './IdiomsGridEditor.jsx';

const clamp = (v, min, max) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
};

export default function FieldsIdioms({ idx, qtnIndex: i }) {
  const { values, setFieldValue } = useFormikContext();
  const base = `questionSets[${idx}].questions.${i}`;
  const q = (values.questionSets && values.questionSets[idx] && values.questionSets[idx].questions && values.questionSets[idx].questions[i]) || {};

  const gridSize = Array.isArray(q.gridSize) ? q.gridSize.slice(0, 2) : [7, 7];
  const tokens = Array.isArray(q.tokens) ? q.tokens : [];
  const [tokensText, setTokensText] = useState(tokens.join(','));

  useEffect(() => {
    setTokensText(tokens.join(','));
  }, [tokens]);

  return (
    <div>
      <h5>Idiom Fill Options / 成語填空設定</h5>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>Grid Size (rows × cols) — max 12×12 / 網格大小（行 × 列）— 最大 12×12</label>
        <input
          type="number"
          min={1}
          max={12}
          value={typeof gridSize[0] !== 'undefined' ? gridSize[0] : ''}
          onChange={(e) => {
            // allow empty string while typing; coerce on blur
            setFieldValue(`${base}.gridSize`, [e.target.value, gridSize[1] || 7]);
          }}
          onBlur={(e) => {
            const r = clamp(e.target.value, 1, 12);
            setFieldValue(`${base}.gridSize`, [r, gridSize[1] || 7]);
          }}
          style={{ width: 80, marginRight: 8 }}
        />
        <input
          type="number"
          min={1}
          max={12}
          value={typeof gridSize[1] !== 'undefined' ? gridSize[1] : ''}
          onChange={(e) => {
            setFieldValue(`${base}.gridSize`, [gridSize[0] || 7, e.target.value]);
          }}
          onBlur={(e) => {
            const c = clamp(e.target.value, 1, 12);
            setFieldValue(`${base}.gridSize`, [gridSize[0] || 7, c]);
          }}
          style={{ width: 80 }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>Tokens (comma-separated Chinese characters)</label>
        <textarea
          rows={2}
          value={tokensText}
          onChange={(e) => {
            // allow free editing (including typing commas); sync to Formik on blur
            setTokensText(e.target.value);
          }}
          onBlur={() => {
            const arr = tokensText.split(',').map((s) => s.trim()).filter((s) => s !== '');
            setFieldValue(`${base}.tokens`, arr);
          }}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <IdiomsGridEditor base={base} gridSize={gridSize} idioms={q.idioms} />
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: 6 }}>Idioms (placements) / 成語位置</label>
        <FieldArray name={`${base}.idioms`}>{(fa) => (
          <div>
            {(q.idioms && q.idioms.length > 0) ? q.idioms.map((idiom, idxId) => (
              <div key={idxId} style={{
                border: '1px solid #eee', padding: 8, marginBottom: 8, borderRadius: 6,
              }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#666' }}>Start [row,col] / 開始 [行,列]</div>
                    <div>
                      <input type="number" min={0} value={(idiom.start && idiom.start[0]) || 0} readOnly style={{ width: 64, marginRight: 6 }} />
                      <input type="number" min={0} value={(idiom.start && idiom.start[1]) || 0} readOnly style={{ width: 64 }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: '#666' }}>End [row,col] / 結束 [行,列]</div>
                    <div>
                      <input type="number" min={0} value={(idiom.end && idiom.end[0]) || 0} readOnly style={{ width: 64, marginRight: 6 }} />
                      <input type="number" min={0} value={(idiom.end && idiom.end[1]) || 0} readOnly style={{ width: 64 }} />
                    </div>
                  </div>

                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Direction / 方向</div>
                    <select value={idiom.direction || 'horizontal'} disabled>
                      <option value="horizontal">horizontal / 橫向</option>
                      <option value="vertical">vertical / 縱向</option>
                    </select>
                  </div>

                  <div style={{ marginLeft: 'auto' }}>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fa.remove(idxId)}>Remove / 刪除</button>
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Pattern (comma-separated, use empty entry for blank slots) / 模式（以逗號分隔，空項表示空白格）</div>
                  <input
                    type="text"
                    value={(idiom.pattern || []).join(',')}
                    readOnly
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Answers (comma-separated for blanks) / 答案（以逗號分隔，用於空白）</div>
                  <input
                    type="text"
                    value={(idiom.answers || []).join(',')}
                    onChange={(e) => setFieldValue(`${base}.idioms.${idxId}.answers`, e.target.value.split(',').map((s) => s.trim()))}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Matches / metadata (optional, comma-separated numbers) / 匹配 / 元資料（可選，逗號分隔數字）</div>
                  <input type="text" value={(idiom.matches || []).join(',')} onChange={(e) => setFieldValue(`${base}.idioms.${idxId}.matches`, e.target.value.split(',').map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n)))} style={{ width: '100%' }} />
                </div> */}
              </div>
            )) : <div style={{ color: '#666', marginBottom: 8 }}>No idioms added yet / 尚未新增成語</div>}

          </div>
        )}</FieldArray>
      </div>
    </div>
  );
}
